import { useCallback } from 'react'
import { User, Office } from '../types'

interface StartChatCallbacks {
  onSuccess?: () => void
  onError?: (error: any) => void
  onFinally?: () => void
}

interface UseStartSalesforceLiveChatProps {
  user: User | null
  office: Office | null
  callbacks?: StartChatCallbacks
}

/**
 * A custom React hook that provides an API to start a new Salesforce Live Chat.
 * This hook accepts user and office data as parameters from the consuming application.
 *
 * @param props - Object containing user data, office data, and optional callbacks.
 * @returns A function `startChat` that, when called, attempts to launch the chat.
 */
export const useStartSalesforceLiveChat = ({ user, office, callbacks }: UseStartSalesforceLiveChatProps) => {
  const startChat = useCallback(async () => {
    if (!window.embeddedservice_bootstrap) {
      callbacks?.onError?.('Unable to load Support Chat. The chat service is not available.')
      return
    }

    if (!user || !office) {
      return
    }

    try {
      await window.embeddedservice_bootstrap.utilAPI.launchChat()

      callbacks?.onSuccess?.()
    } catch (error) {
      callbacks?.onError?.('Unable to load Support Chat. An unknown error occurred loading the chat service.')
      throw new Error(error instanceof Error ? error.message : String(error))
    } finally {
      callbacks?.onFinally?.()
    }
  }, [user, office, callbacks])

  return startChat
}
